import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ArrowLeft, Zap } from "lucide-react";
import { BrandLogo } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { signInWithSocialPulse, firebaseReady } from "@/lib/firebase";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login, register, ssoComplete } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "bg-white border-slate-200 rounded-lg h-12 focus-visible:ring-brand";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (mode === "login") u = await login(form.email, form.password);
      else u = await register({ name: form.name, email: form.email, password: form.password, company: form.company });
      toast.success("Welcome to My WebsiteTech");
      navigate(u?.role === "admin" ? "/admin" : u?.role === "editor" ? "/admin/cms" : u?.role === "partner" ? "/kerala/partner-portal" : "/portal");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const socialPulseLogin = async () => {
    if (!firebaseReady) {
      toast.error("SocialPulse SSO is not configured for this deployment.");
      return;
    }
    setSsoLoading(true);
    try {
      const { idToken } = await signInWithSocialPulse();
      const { data } = await axios.post(`${API_BASE}/api/auth/firebase-login`, { id_token: idToken });
      ssoComplete(data.token, data.user);
      toast.success(data.new_user ? "Welcome to SocialPulse — your account is ready." : "Welcome back!");
      const u = data.user;
      navigate(u?.role === "admin" ? "/admin" : u?.role === "editor" ? "/admin/cms" : u?.role === "partner" ? "/kerala/partner-portal" : "/portal");
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/popup-closed-by-user") {
        // silent — user cancelled
      } else if (code === "auth/popup-blocked") {
        toast.error("Popup blocked — please allow popups for this site.");
      } else {
        toast.error(formatErr(err.response?.data?.detail) || err.message || "SocialPulse sign-in failed.");
      }
    } finally {
      setSsoLoading(false);
    }
  };

  return (
    <main className="min-h-screen hero-bg flex flex-col">
      <div className="p-6"><Link to="/" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-brand text-sm"><ArrowLeft size={14} /> Back to site</Link></div>
      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="flex justify-center mb-6"><BrandLogo size={44} /></div>
          <div className="bg-white rounded-2xl soft-shadow-lg border border-slate-100 p-8">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{mode === "login" ? "Log in to My WebsiteTech" : "Create your account"}</h1>
            <p className="text-slate-500 text-sm mt-1">{mode === "login" ? "Track your sites, projects, analytics and leads." : "Start tracking your website performance."}</p>

            <form onSubmit={submit} className="space-y-3 mt-6">
              {mode === "register" && (
                <>
                  <Input data-testid="auth-name" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} required />
                  <Input data-testid="auth-company" placeholder="Company (optional)" value={form.company} onChange={set("company")} className={inputCls} />
                </>
              )}
              <Input data-testid="auth-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} required />
              <Input data-testid="auth-password" type="password" placeholder="Password" value={form.password} onChange={set("password")} className={inputCls} required />
              <button type="submit" disabled={loading} data-testid="auth-submit" className="btn-primary rounded-lg w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <>{mode === "login" ? "Log In" : "Create Account"} <ArrowRight size={16} /></>}
              </button>
            </form>

            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sm text-brand mt-4 hover:underline">
              {mode === "login" ? "Need an account? Sign up" : "Already have an account? Log in"}
            </button>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px bg-slate-200 flex-1" />
              <span className="text-xs text-slate-400">or</span>
              <span className="h-px bg-slate-200 flex-1" />
            </div>
            <button
              type="button"
              onClick={socialPulseLogin}
              disabled={ssoLoading}
              data-testid="sso-socialpulse"
              className="relative w-full rounded-lg p-[2px] bg-gradient-to-r from-[#A855F7] to-[#4ADE80] hover:shadow-[0_0_18px_rgba(168,85,247,0.28)] transition-shadow group disabled:opacity-70"
            >
              <span className="flex items-center justify-center gap-2 w-full py-3 rounded-[7px] bg-white text-slate-800 font-semibold group-hover:bg-slate-50 transition-colors">
                {ssoLoading ? <Loader2 className="animate-spin text-[#A855F7]" size={16} />
                            : <Zap size={16} className="text-[#A855F7]" />}
                {ssoLoading ? "Signing you in…" : "Continue with SocialPulse"}
              </span>
            </button>

            {mode === "login" && (
              <div className="mt-6 border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Demo client account</p>
                <p className="text-xs text-slate-600 font-mono">demo@client.com · Demo@1234</p>
              </div>
            )}
          </div>
          <p className="text-center text-xs text-slate-400 mt-4">Secured by SocialPulse SSO · websitetech.pro</p>
        </motion.div>
      </div>
    </main>
  );
}
